import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AsyncPipe,
  CurrencyPipe,
  DecimalPipe,
  PercentPipe,
} from '@angular/common';
import {MatTableModule} from '@angular/material/table';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {SatPopoverModule} from '@ncstate/sat-popover';
import {CaregiverPerformanceGridDatasource} from './caregiver-performance-grid.datasource';
import {CaregiverManagementDashboardService} from '../../../services/caregiver-management-dashboard.service';
import {first, fromEvent, Observable, ReplaySubject} from 'rxjs';
import {AuthService} from '../../../services/auth.service';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {debounceTime, map} from 'rxjs/operators';
import {ApiSortDirection} from '../../../models/pagination';
import {FilterService} from '../../../services/filter.service';
import {UserPreferenceService} from '../../../services/user-preference.service';
import {UserService} from '../../../services/user.service';
import {User} from '../../../models/user';
import {
  CaregiverDto,
  TurnoverRiskStratificationDescriptions,
} from '../../../models/caregiver-dto';
import {FeatureFlagService} from '../../../services/feature-flag.service';
import {FeatureFlagDirective} from '../../../core/feature-flag.directive';
import {CaregiverNameDisplayComponent} from '../caregiver-name-display/caregiver-name-display.component';
import {TurnoverRiskFactorsPopoverComponent} from './turnover-risk-factors-popover/turnover-risk-factors-popover.component';
import {TurnoverRiskComponent} from '../turnover-risk/turnover-risk.component';
import {TenureDisplayComponent} from '../tenure-display/tenure-display.component';
import {UtilizationDisplayComponent} from '../utilization-display/utilization-display.component';
import {AvgDailyMileageDisplayComponent} from '../avgdailymileage-display/avgdailymileage-display.component';
import {CanceledShiftsComponent} from '../canceled-shifts/canceled-shifts.component';
import {TardinessDisplayComponent} from '../tardiness-display/tardiness-display.component';
import {HighHospRiskComponent} from '../high-hosp-risk/high-hosp-risk.component';
import {CaregiverPerformanceGridEmptyStateComponent} from '../caregiver-performance-grid-empty-state/caregiver-performance-grid-empty-state.component';

@UntilDestroy()
@Component({
  selector: 'app-caregiver-performance-grid',
  templateUrl: './caregiver-performance-grid.component.html',
  styleUrls: ['./caregiver-performance-grid.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    PercentPipe,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    SatPopoverModule,
    FeatureFlagDirective,
    CaregiverNameDisplayComponent,
    TurnoverRiskFactorsPopoverComponent,
    TurnoverRiskComponent,
    TenureDisplayComponent,
    UtilizationDisplayComponent,
    AvgDailyMileageDisplayComponent,
    CanceledShiftsComponent,
    TardinessDisplayComponent,
    HighHospRiskComponent,
    CaregiverPerformanceGridEmptyStateComponent,
  ],
})
export class CaregiverPerformanceGridComponent
  implements OnInit, AfterViewInit {
  private caregiverManagementDashboardService = inject(
    CaregiverManagementDashboardService
  );
  private authService = inject(AuthService);
  private router = inject(Router);
  private filterService = inject(FilterService);
  private userPreferenceService = inject(UserPreferenceService);
  private userService = inject(UserService);
  private featureFlagService = inject(FeatureFlagService);

  displayedColumns: string[] = [];

  @Output() allowExportToCsv = new EventEmitter<boolean>();

  @ViewChild('sortCaregivers', {static: true}) sort: MatSort | null = null;

  @Output() sortChanged = new EventEmitter<MatSort>();

  @ViewChild(MatPaginator, {static: true})
  paginator: MatPaginator | null = null;

  @ViewChild('tableContainer')
  tableContainer: ElementRef<HTMLDivElement> | null = null;

  dataSource: CaregiverPerformanceGridDatasource;
  user: User;
  turnoverRiskPopoverEnabled$: Observable<boolean>;

  private tableWidth: number;
  private gridParameterChanges$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor() {
    const caregiverManagementDashboardService = this
      .caregiverManagementDashboardService;
    const featureFlagService = this.featureFlagService;

    this.dataSource = new CaregiverPerformanceGridDatasource(
      caregiverManagementDashboardService
    );
    this.turnoverRiskPopoverEnabled$ = featureFlagService
      .get('tipc_dash_grid_turnover_risk_factors_popover')
      .pipe(
        first(),
        map((flagVarient) => flagVarient === 'on')
      );
  }

  ngOnInit(): void {
    this.paginator._intl.itemsPerPageLabel = 'Rows per page:';
    this.initializeColumnDisplaySubscription();
    this.initializeCombinedSubscription();
    this.dataSource.totalCount$.subscribe((totalCount) => {
      this.allowExportToCsv.emit(totalCount !== 0);
    });
    this.sort.sortChange.subscribe(() => {
      this.sortChanged.emit(this.sort);
    });
    this.userService.user$.subscribe((user) => (this.user = user));
  }

  ngAfterViewInit(): void {
    // Subscribe to window resize events, so we can alter display columns as needed
    fromEvent(window, 'resize')
      .pipe(untilDestroyed(this), debounceTime(100))
      .subscribe(() => this.updateTableWidth());

    // Defer to next tick to avoid changing displayedColumns expression in middle of view rendering
    setTimeout(() => this.updateTableWidth());
  }

  loadListPage() {
    const sortDirection = this.sort?.direction || ApiSortDirection.Ascending;
    this.dataSource.loadList(this.filterService.currentSelections, {
      page: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 20,
      sortBy: this.sort?.active,
      sortDirection: sortDirection as ApiSortDirection,
    });
    this.sortChanged.emit(this.sort);
  }

  private updateTableWidth(): void {
    this.tableWidth = this.tableContainer?.nativeElement.clientWidth ?? 0;
  }

  private initializeColumnDisplaySubscription() {
    this.userPreferenceService.userPreferences$
      .pipe(
        map((userPreferences) =>
          userPreferences.caregiverDashboardColumns
            .filter((cdc) => !cdc.hideColumn)
            .map((cdc) => cdc.column)
        )
      )
      .subscribe(
        (columnsToDisplay) => (this.displayedColumns = columnsToDisplay)
      );
  }

  private initializeCombinedSubscription() {
    this.paginator?.page.subscribe(() => this.gridParameterChanges$.next(true));
    this.sort?.sortChange.subscribe(() => {
      this.paginator?.firstPage();
      this.gridParameterChanges$.next(true);
    });
    this.filterService.currentSelectionsChange$.subscribe(() =>
      this.gridParameterChanges$.next(true)
    );
    this.gridParameterChanges$
      .pipe(debounceTime(100))
      .subscribe(() => this.loadListPage());
  }
}
